import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import { useSendInvoiceMail, useInvoicePrefillEmail } from '@/api/invoices';

interface MailForm {
  recipients: string;
  cc: string;
  bcc: string;
}

const EMPTY: MailForm = { recipients: '', cc: '', bcc: '' };

/** Pull a string field off the prefill result, tolerating missing keys. */
function prefillStr(result: Record<string, unknown> | undefined, key: string): string {
  const v = result?.[key];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Send-mail slide-over — recipients / CC / BCC, prefilled on open from
 * `/invoices/:id/prefill-email` (mailer_receiver/cc/bcc, falling back to the
 * customer email). Posts `{ recipients, cc, bcc }` via `useSendInvoiceMail`.
 */
export function InvoiceSendMailDrawer({
  id,
  open,
  onOpenChange,
}: {
  id: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [form, setForm] = useState<MailForm>(EMPTY);
  const [touched, setTouched] = useState(false);

  const { data: prefill, isLoading: prefillLoading } = useInvoicePrefillEmail(id, open);
  const sendMail = useSendInvoiceMail(id);

  // Seed the form from prefill once per open; user edits are preserved after.
  useEffect(() => {
    if (!open) {
      setForm(EMPTY);
      setTouched(false);
      return;
    }
    const result = prefill?.result;
    if (!result || touched) return;
    setForm({
      recipients: prefillStr(result, 'mailer_receiver') || prefillStr(result, 'customer_email'),
      cc: prefillStr(result, 'mailer_cc'),
      bcc: prefillStr(result, 'mailer_bcc'),
    });
  }, [open, prefill, touched]);

  const set = (k: keyof MailForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(true);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const onSend = () => {
    const recipients = form.recipients.trim();
    if (!recipients) return;
    sendMail.mutate(
      { recipients, cc: form.cc.trim(), bcc: form.bcc.trim() },
      {
        onSuccess: () => {
          toast.success('Invoice email sent');
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : 'Failed to send email';
          toast.error('Failed to send email', { description: message });
        },
      },
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)', padding: 'var(--vn-space-5)', overflowY: 'auto' }}>
          <div>
            <DrawerTitle>Send invoice email</DrawerTitle>
            <DrawerDescription>
              Deliver this invoice to the customer. Separate multiple addresses with commas.
            </DrawerDescription>
          </div>

          {prefillLoading ? <Alert tone="info" title="Loading recipient defaults…" /> : null}

          <Field label="Recipients" required>
            {(p) => (
              <Input {...p} value={form.recipients} onChange={set('recipients')} placeholder="name@customer.com" />
            )}
          </Field>
          <Field label="CC">
            {(p) => <Input {...p} value={form.cc} onChange={set('cc')} placeholder="cc@customer.com" />}
          </Field>
          <Field label="BCC">
            {(p) => <Input {...p} value={form.bcc} onChange={set('bcc')} placeholder="bcc@customer.com" />}
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={onSend} loading={sendMail.isPending} disabled={!form.recipients.trim()}>
              Send
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
